import { Module, PATH_PROJECT_WITH_ID } from "@/constants";
import {
  basicListingProject,
  fullListingProject,
  PROJECT_STATUS,
  PROJECT_STATUS_DELETED,
  PLANNED_DURATION_PAST_SCOPES,
  PLANNED_DURATION_FUTURE_SCOPES,
} from "@/schemas/project";
import { ReadProject } from "@/types/project";
import { ColumnDef, Row } from "@tanstack/react-table";
import i18n from "@/i18n";
import StandardQueryTable from "@/components/StandardQueryTable";
import { ProjectService } from "../service/project";
import { EnumFilter } from "@/components/data-table/components/EnumFilter";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { PROJECT_DEPENDS_ON } from "@/schemas/commonFields";
import { generatePath } from "react-router-dom";
import { useMemo } from "react";
import { getRowPropsWithDeletedHighlight } from "@/lib/table/getRowPropsWithDeletedHighlight";

const plannedDurationScopeColumn = (
  direction: "past" | "future",
  options: readonly string[],
): ColumnDef<ReadProject, number> => {
  const field =
    `contractor_visits_planned_duration_${direction}_scope` as const;
  const translationKey = `projects.plannedDurationVisibility.${direction}`;
  return {
    id: field,
    cell: ({ row }) => {
      const value = row.original[field];
      return row.original.contractor_visits_planned_duration_visible && value
        ? i18n.t(`${translationKey}.${value}`)
        : null;
    },
    meta: {
      filter: {
        component: EnumFilter,
        props: { options, translationKey },
      },
    },
  };
};

const ProjectList = () => {
  const permissions = useModulePermissions(Module.Projects);
  const schema = permissions.projects_read_full
    ? fullListingProject
    : basicListingProject;

  const columnOverrides = {
    ...(permissions.projects_read_full && {
      contractor_visits_planned_duration_past_scope: plannedDurationScopeColumn(
        "past",
        PLANNED_DURATION_PAST_SCOPES,
      ),
      contractor_visits_planned_duration_future_scope:
        plannedDurationScopeColumn("future", PLANNED_DURATION_FUTURE_SCOPES),
      status: {
        id: "status",
        cell: ({ row }: { row: Row<ReadProject> }) =>
          i18n.t(`projectStatuses.${row.original.status}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_STATUS,
              translationKey: "projectStatuses",
            },
          },
        },
      },
      project_depends_on: {
        id: "project_depends_on",
        cell: ({ row }: { row: Row<ReadProject> }) =>
          i18n.t(`projectDependsOn.${row.original.project_depends_on}`),
        meta: {
          filter: {
            component: EnumFilter,
            props: {
              options: PROJECT_DEPENDS_ON,
              translationKey: "projectDependsOn",
            },
          },
        },
      },
    }),
  };

  const tableFeatures = useMemo(
    () => ({
      exportSettings: { moduleName: Module.Projects },
      initialColumnVisibility: {
        contractor_visits_planned_duration_past_scope: false,
        contractor_visits_planned_duration_future_scope: false,
      },
      enableShowDeletedToggle: true,
      getRowProps: getRowPropsWithDeletedHighlight<ReadProject>(
        (row) => row.status === PROJECT_STATUS_DELETED,
      ),
    }),
    [],
  );

  return (
    <StandardQueryTable<ReadProject>
      schema={schema}
      name={Module.Projects}
      fetchList={ProjectService.getList}
      pathBuilder={(row) =>
        generatePath(PATH_PROJECT_WITH_ID, {
          projectId: row.id,
        })
      }
      tableFeatures={tableFeatures}
      columnOverrides={columnOverrides}
    />
  );
};

export default ProjectList;
